'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { Search, ChevronDown, ArrowRight, Loader2 } from 'lucide-react'
import { RichText } from '../RichText'
import { Media, VisaType, Persona } from '@/payload-types'
import { getMediaUrl } from '../../lib/media'

const CONTENT_TYPES = [
  { id: 'pdf-guides', label: 'PDF Guides' },
  { id: 'articles', label: 'News & Articles' },
  { id: 'videos', label: 'Videos' },
  { id: 'tools-calculators', label: 'Tools & Calculators' },
]

interface ResourcesHeroProps {
  heroHeading: any
  heroDetail?: any
  heroBackgroundImage?: Media
  searchPlaceholder?: string
  visaTypes?: VisaType[]
  personas?: Persona[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  searchLoading?: boolean
}

export function ResourcesHero({
  heroHeading,
  heroDetail,
  heroBackgroundImage,
  searchPlaceholder,
  visaTypes = [],
  personas = [],
  searchQuery: controlledQuery,
  onSearchChange,
  searchLoading = false,
}: ResourcesHeroProps) {
  const [internalQuery, setInternalQuery] = useState('')
  const searchQuery = controlledQuery ?? internalQuery
  const setSearchQuery = onSearchChange ?? setInternalQuery
  const [selectedVisaTypes, setSelectedVisaTypes] = useState<string[]>([])
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([])
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<string>('newest')
  const [visaTypeOpen, setVisaTypeOpen] = useState(false)
  const [personaOpen, setPersonaOpen] = useState(false)
  const [contentTypeOpen, setContentTypeOpen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)

  const toggleVisaType = (id: string) => {
    setSelectedVisaTypes((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    )
  }

  const togglePersona = (id: string) => {
    setSelectedPersonas((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    )
  }

  const toggleContentType = (id: string) => {
    setSelectedContentTypes((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    )
  }

  const getVisaTypeLabel = () => {
    if (selectedVisaTypes.length === 0) return 'All'
    if (selectedVisaTypes.length === 1) {
      return visaTypes.find((v) => v.id.toString() === selectedVisaTypes[0])?.name || 'All'
    }
    return `${selectedVisaTypes.length} selected`
  }

  const getPersonaLabel = () => {
    if (selectedPersonas.length === 0) return 'All'
    if (selectedPersonas.length === 1) {
      return personas.find((p) => p.id.toString() === selectedPersonas[0])?.name || 'All'
    }
    return `${selectedPersonas.length} selected`
  }

  const getContentTypeLabel = () => {
    if (selectedContentTypes.length === 0) return 'All'
    if (selectedContentTypes.length === 1) {
      return CONTENT_TYPES.find((c) => c.id === selectedContentTypes[0])?.label || 'All'
    }
    return `${selectedContentTypes.length} selected`
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
  }

  return (
    <>
      <section className="relative text-white">
        {/* Background Image Section */}
        <div className="relative flex items-center min-h-[40vh] md:min-h-[50vh]">
          {/* Background Image */}
          {getMediaUrl(heroBackgroundImage, 'hero') && (
            <div className="absolute inset-0">
              <Image
                src={getMediaUrl(heroBackgroundImage, 'hero')}
                alt={heroBackgroundImage?.alt || 'Resources hero background'}
                fill
                sizes="100vw"
                className="object-cover object-right"
                priority
              />
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/40 to-black/20" />

          <div className="relative z-10 max-w-380 mx-auto px-6 md:px-12 py-24 w-full">
            <div className="max-w-3xl">
              {heroHeading && (
                <RichText
                  data={heroHeading}
                  className="prose prose-invert text-3xl md:text-4xl lg:text-5xl font-medium leading-tight mt-12 mb-8 [&_p]:text-white [&_span.font-serif]:text-white [&_span.font-serif]:italic [&_span.font-serif]:underline [&_span.font-serif]:decoration-2 [&_span.font-serif]:underline-offset-4"
                />
              )}

              {heroDetail && (
                <RichText
                  data={heroDetail}
                  className="prose prose-invert mb-0 2xl:mb-12 [&_p]:text-white/90 [&_p]:text-base [&_p]:md:text-lg [&_p]:leading-relaxed"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Search Bar - straddles hero/content boundary */}
      <div className="relative z-20 -mt-8 mb-8 w-full">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSearch} className="relative mb-4 md:mb-6">
            <div className="relative bg-white rounded-full overflow-hidden shadow-lg">
              <div className="flex items-center px-4 sm:px-6 py-3 sm:py-4">
                <Search className="w-5 h-5 text-gray-500 mr-3 shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder || 'What are you looking for today?'}
                  className="flex-1 outline-none text-gray-900 placeholder:text-gray-500 text-sm sm:text-base bg-transparent"
                />
                <button
                  type="submit"
                  className="ml-3 sm:ml-4 w-10 h-10 rounded-full bg-brand-blue hover:bg-brand-blue/90 transition-colors flex items-center justify-center shrink-0"
                >
                  {searchLoading ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <ArrowRight className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Filter Pills - full-bleed scroll on mobile */}
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-3 items-center text-sm text-gray-700 w-max sm:w-full sm:flex-wrap sm:max-w-5xl sm:mx-auto px-6 md:px-12 pb-2 sm:pb-0">
            {/* Content Type Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600 whitespace-nowrap">Content Type:</span>
              <div className="relative">
                <button
                  onClick={() => {
                    setContentTypeOpen(!contentTypeOpen)
                    setVisaTypeOpen(false)
                    setPersonaOpen(false)
                    setSortOpen(false)
                  }}
                  className="px-4 py-1.5 rounded-full bg-white hover:bg-gray-200 transition-colors border border-gray-300 flex items-center gap-2 min-w-25 justify-between whitespace-nowrap"
                >
                  <span className="capitalize">{getContentTypeLabel()}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {contentTypeOpen && (
                  <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-55 max-h-75 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedContentTypes([])
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors first:rounded-t-lg border-b border-gray-200"
                    >
                      Clear All
                    </button>
                    {CONTENT_TYPES.map((contentType) => (
                      <label
                        key={contentType.id}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition-colors cursor-pointer last:rounded-b-lg"
                      >
                        <input
                          type="checkbox"
                          checked={selectedContentTypes.includes(contentType.id)}
                          onChange={() => toggleContentType(contentType.id)}
                          className="w-4 h-4 text-brand-blue rounded border-gray-300 focus:ring-brand-blue"
                        />
                        <span>{contentType.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Visa Type Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600 whitespace-nowrap">Visa Type:</span>
              <div className="relative">
                <button
                  onClick={() => {
                    setVisaTypeOpen(!visaTypeOpen)
                    setContentTypeOpen(false)
                    setPersonaOpen(false)
                    setSortOpen(false)
                  }}
                  className="px-4 py-1.5 rounded-full bg-white hover:bg-gray-200 transition-colors border border-gray-300 flex items-center gap-2 min-w-25 justify-between whitespace-nowrap"
                >
                  <span className="capitalize">{getVisaTypeLabel()}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {visaTypeOpen && (
                  <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-50 max-h-75 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedVisaTypes([])
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors first:rounded-t-lg border-b border-gray-200"
                    >
                      Clear All
                    </button>
                    {visaTypes.map((visaType) => (
                      <label
                        key={visaType.id}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition-colors cursor-pointer last:rounded-b-lg"
                      >
                        <input
                          type="checkbox"
                          checked={selectedVisaTypes.includes(visaType.id.toString())}
                          onChange={() => toggleVisaType(visaType.id.toString())}
                          className="w-4 h-4 text-brand-blue rounded border-gray-300 focus:ring-brand-blue"
                        />
                        <span>{visaType.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Persona/Made For Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600 whitespace-nowrap">Made for:</span>
              <div className="relative">
                <button
                  onClick={() => {
                    setPersonaOpen(!personaOpen)
                    setContentTypeOpen(false)
                    setVisaTypeOpen(false)
                    setSortOpen(false)
                  }}
                  className="px-4 py-1.5 rounded-full bg-white hover:bg-gray-200 transition-colors border border-gray-300 flex items-center gap-2 min-w-25 justify-between whitespace-nowrap"
                >
                  <span className="capitalize">{getPersonaLabel()}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {personaOpen && (
                  <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-50 max-h-75 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedPersonas([])
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors first:rounded-t-lg border-b border-gray-200"
                    >
                      Clear All
                    </button>
                    {personas.map((persona) => (
                      <label
                        key={persona.id}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition-colors cursor-pointer last:rounded-b-lg"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPersonas.includes(persona.id.toString())}
                          onChange={() => togglePersona(persona.id.toString())}
                          className="w-4 h-4 text-brand-blue rounded border-gray-300 focus:ring-brand-blue"
                        />
                        <span>{persona.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sort By Dropdown */}
            <div className="sm:ml-auto flex items-center gap-2">
              <span className="text-gray-600 whitespace-nowrap">Sort By:</span>
              <div className="relative">
                <button
                  onClick={() => {
                    setSortOpen(!sortOpen)
                    setContentTypeOpen(false)
                    setVisaTypeOpen(false)
                    setPersonaOpen(false)
                  }}
                  className="px-4 py-1.5 rounded-full bg-white hover:bg-gray-200 transition-colors border border-gray-300 flex items-center gap-2 min-w-25 justify-between whitespace-nowrap"
                >
                  <span className="capitalize">{sortBy}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {sortOpen && (
                  <div className="absolute top-full mt-1 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-37.5">
                    <button
                      onClick={() => {
                        setSortBy('newest')
                        setSortOpen(false)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors first:rounded-t-lg"
                    >
                      Newest
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('oldest')
                        setSortOpen(false)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                    >
                      Oldest
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('a-z')
                        setSortOpen(false)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors last:rounded-b-lg"
                    >
                      A-Z
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
